import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import logger from '../../logger';
import Showcase from './Showcase';
// コンテンツ列の幅 (300px) + 左右マージン (48px × 2) = 396px
const CONTENT_COLUMN_WIDTH = 396;
// 右下のカメラ小窓 (_premeeting-screens.scss の .deepai-prejoin #preview) を
// 避けるための下余白。小窓の高さ135px + 下24px + 余白。項目が増えて縦に伸びた
// ときに、最後の1件が小窓の裏に潜り込まないようにする。
const CAMERA_THUMB_CLEARANCE = 190;
// ホバー拡大の枠。16:9 で固定し、中身は contain で収める。画像の縦横比は
// 読み込むまで分からないので、枠を可変にすると出た瞬間に位置が飛ぶ。
const PREVIEW_W = 520;
const PREVIEW_H = 293;
// 枠と画面端 / サムネイルとの間隔。
const PREVIEW_GAP = 12;
const EMPTY = {
    absent: false,
    demoVideos: [],
    err: null,
    loading: true,
    materials: [],
    summary: '',
    themeUrl: null,
    title: ''
};
/*
 * 見た目は deepai-theme の CSS 変数だけで決める（html[data-theme] に載っている）。
 * Jitsi 標準の暗色パレット(theme.palette.text01 など)を使うと、生成りの natural
 * テーマの上に白い文字を置くことになって読めなくなる。変数を通せばテーマ切替に
 * そのまま追従する。
 *
 * var() の第2引数は :root の既定値（テーマ未適用＝素の暗色）と同じものを入れて
 * ある。スタイルシートが何かの理由で入らなくても、暗色UIとして成立させるため。
 */
const useStyles = makeStyles()(() => {
    return {
        root: {
            position: 'fixed',
            top: 0,
            left: CONTENT_COLUMN_WIDTH,
            right: 0,
            bottom: 0,
            zIndex: 253,
            overflowY: 'auto',
            padding: `48px 56px ${CAMERA_THUMB_CLEARANCE}px`,
            color: 'var(--text, #eaf0ff)',
            font: '400 15px/1.7 var(--font, system-ui, sans-serif)',
            '@media (max-width: 720px)': {
                position: 'static',
                padding: '24px 16px 32px'
            }
        },
        /*
         * 部屋のテーマ画像。root 自体に background を置くとスクロールの扱いが
         * 環境で割れるので、動かない層を1枚敷いて、その上に中身を載せる。
         *
         * ::after は文字を守るための膜。左（本文がある側）だけ地色で覆い、
         * 右へ向けて透明にする。全面に掛けると画像が見えず、掛けないと
         * 明るい画像でタイトルが読めなくなる（カメラ映像で一度やった）。
         */
        bgLayer: {
            position: 'fixed',
            top: 0,
            left: CONTENT_COLUMN_WIDTH,
            right: 0,
            bottom: 0,
            zIndex: 0,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(90deg, var(--surface, rgba(20,28,54,.55)) 0%, transparent 58%)',
                // 全体の膜は控えめにする。ここを濃くすると画像が見えなくなる。
                // 文字の読みやすさはタイトル自身の下地(titleOnImage)で担保する。
                opacity: 0.72
            },
            // 狭い画面ではパネルが通常フローに落ちる。全面の画像は成立しない。
            '@media (max-width: 720px)': {
                display: 'none'
            }
        },
        // 画面が広くても本文が横に伸び切らないように。1行が長いほど読みにくい。
        inner: {
            maxWidth: 720,
            // 背景レイヤより前。
            position: 'relative',
            zIndex: 1
        },
        /*
         * 業務名はこの画面の主題なので、ここが一番強い。
         * 元は 13px の薄いラベルにしていたが、それだと本文より弱く、
         * 見出しがさらに弱いという逆転になっていた。
         */
        businessTitle: {
            color: 'var(--text, #eaf0ff)',
            fontFamily: 'var(--font-head, var(--font, system-ui, sans-serif))',
            fontSize: 27,
            fontWeight: 600,
            letterSpacing: '.01em',
            lineHeight: 1.4,
            margin: '0 0 14px'
        },
        /*
         * テーマ画像がある時だけタイトルに敷く下地。
         *
         * 全体に掛ける膜だけでは足りない。膜は位置で濃さが決まるのに対して、
         * タイトルの長さは業務名しだいで変わるので、長い名前は薄い側へはみ出す。
         * 文字と同じ幅で付いてくる下地なら、名前が何文字でも読める。
         */
        titleOnImage: {
            background: 'var(--surface, rgba(20,28,54,.55))',
            borderRadius: 'var(--radius-sm, 14px)',
            boxShadow: 'var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7))',
            display: 'inline-block',
            padding: '8px 18px',
            // --surface が半透明のテーマでも背後の絵が透けないようにする。
            backdropFilter: 'blur(18px) saturate(140%)'
        },
        // タイトル直下の短い罫。テーマの差し色で、画面が誰のものか分かるようにする。
        titleRule: {
            background: 'var(--accent, #246FE5)',
            borderRadius: 2,
            height: 3,
            marginBottom: 28,
            width: 44
        },
        card: {
            background: 'var(--surface, rgba(20,28,54,.55))',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: 'var(--radius, 22px)',
            boxShadow: 'var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7))',
            marginBottom: 20,
            padding: '26px 30px'
        },
        /*
         * カードの見出しは「この中身は何か」を示すラベル。タイトルと張り合わせない。
         * 主役はカードの中身（概要の本文・動画・資料）で、見出しはその上に小さく置く。
         */
        heading: {
            color: 'var(--text-dim, #93a0c4)',
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: '.14em',
            margin: '0 0 14px'
        },
        summary: {
            fontSize: 16,
            lineHeight: 1.95,
            margin: 0,
            // 1行 40〜50 文字あたりで折り返す。
            maxWidth: '44em'
        },
        empty: {
            color: 'var(--text-dim, #93a0c4)',
            margin: 0
        },
        errorCard: {
            background: 'var(--surface, rgba(20,28,54,.55))',
            border: '1px solid var(--danger, #D83848)',
            borderRadius: 'var(--radius, 22px)',
            boxShadow: 'var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7))',
            padding: '26px 30px',
            whiteSpace: 'pre-wrap'
        },
        errorTitle: {
            color: 'var(--danger, #D83848)',
            fontFamily: 'var(--font-head, var(--font, system-ui, sans-serif))',
            fontSize: 18,
            fontWeight: 600,
            margin: '0 0 10px'
        },
        errorBody: {
            color: 'var(--text-dim, #93a0c4)',
            fontSize: 13,
            lineHeight: 1.8
        },
        /*
         * 横1列の棚。折り返して段が増えると、カードが縦に伸びて下の内容が
         * 押し出されるので、はみ出したぶんは横へ流す。
         */
        shelf: {
            position: 'relative'
        },
        strip: {
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 20,
            margin: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            // 指で流したときに項目の頭で止まる。
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            // 横棒はカードの中で目障りなので消す。位置は矢印で示す。
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        },
        item: {
            flex: '0 0 auto',
            margin: 0,
            scrollSnapAlign: 'start',
            width: 300
        },
        /*
         * 端の矢印。はみ出している側にだけ出す。
         * サムネイル(300px の 16:9 = 169px)の中心に合わせる。
         */
        arrow: {
            position: 'absolute',
            top: 84,
            zIndex: 2,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            cursor: 'pointer',
            color: 'var(--text, #eaf0ff)',
            background: 'var(--surface, rgba(20,28,54,.55))',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: '50%',
            boxShadow: 'var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7))',
            backdropFilter: 'blur(10px)',
            transition: 'border-color .16s, color .16s',
            '&:hover': {
                borderColor: 'var(--accent, #246FE5)',
                color: 'var(--accent, #246FE5)'
            }
        },
        arrowLeft: {
            left: -18
        },
        arrowRight: {
            right: -18
        },
        // 動画・画像・PDF で枠の見え方を揃える。並べたときにガタつかせない。
        thumb: {
            background: '#000',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: 'var(--radius-sm, 14px)',
            display: 'block',
            // 16:9 を保つ。読み込み前に高さ0で潰れて、カードが跳ねるのを防ぐ。
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            width: '100%'
        },
        // PDF などサムネイルを作れないもの。拡張子を大きく出して中身の代わりにする。
        fileBox: {
            alignItems: 'center',
            background: 'var(--surface-2, rgba(28,38,72,.42))',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: 'var(--radius-sm, 14px)',
            display: 'flex',
            justifyContent: 'center',
            aspectRatio: '16 / 9',
            width: '100%'
        },
        fileKind: {
            color: 'var(--accent, #246FE5)',
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: '.12em'
        },
        // 画像・PDF はクリックで原寸を開く。下線は出さず、枠の色で反応を返す。
        link: {
            color: 'inherit',
            display: 'block',
            textDecoration: 'none',
            '&:hover img, &:hover > div': {
                borderColor: 'var(--accent, #246FE5)'
            }
        },
        itemTitle: {
            color: 'var(--text, #eaf0ff)',
            fontSize: 13.5,
            fontWeight: 600,
            marginTop: 9
        },
        itemDescription: {
            color: 'var(--text-dim, #93a0c4)',
            fontSize: 12.5,
            lineHeight: 1.75,
            marginTop: 3
        },
        /*
         * ホバー拡大。body へ portal している。パネルは overflow:auto なので、
         * ここに置かないとカードの外に出た分が切り落とされる。
         *
         * pointerEvents:none が要る。枠がカーソルの下に入ると、サムネイルから
         * 外れた扱いになって mouseleave→mouseenter を繰り返し、ちらつく。
         */
        preview: {
            position: 'fixed',
            zIndex: 600,
            width: PREVIEW_W,
            height: PREVIEW_H,
            padding: 6,
            pointerEvents: 'none',
            background: 'var(--surface, rgba(20,28,54,.55))',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: 'var(--radius-sm, 14px)',
            boxShadow: 'var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7))',
            backdropFilter: 'blur(16px) saturate(150%)'
        },
        previewImg: {
            display: 'block',
            height: '100%',
            // 縦長でも横長でも全体を見せる。切り取らない。
            objectFit: 'contain',
            width: '100%'
        }
    };
});
/**
 * 拡張子相当の短い名前。サムネイルを作れないファイルの中身の代わりに出す。
 *
 * @param {IAttachment} f - 対象のファイル。
 * @returns {string}
 */
function kindLabelOf(f) {
    const ext = /\.([a-z0-9]{1,5})$/i.exec(f.fileName ?? '')?.[1];
    if (ext) {
        return ext.toUpperCase();
    }
    // 拡張子が無いときは MIME の後ろ半分（application/pdf → PDF）で代用する。
    return (f.contentType ?? '').split('/').pop()?.toUpperCase() || 'ファイル';
}
/**
 * 部屋名から業務情報を引いて、シナリオ概要・デモ動画・参考資料を待機画面に出す。
 *
 * 部屋には業務IDが直接ついていないので2段で引く:
 *
 *   部屋 → 割り当てAI → 業務ID → 業務情報
 *
 * 取得先は config.js の deepai_web_server_url。会議サーバーとは別オリジンなので、
 * 向こう側で CORS が許可されていないとブラウザが通信を止める。その場合は黙って
 * 空になるのではなく、何が起きたのかを画面に出す（利用者が開発者コンソールを
 * 開けるとは限らない）。
 *
 * hearing.scenario は AI への指示書そのもの（怒り度の規則や講評への切り替え条件が
 * 書かれている）なので絶対に出さない。出すのは1行の scenarioSummary だけ。
 *
 * @returns {JSX.Element|null}
 */
const PrejoinPanel = () => {
    const { classes } = useStyles();
    const room = useSelector((state) => state['features/base/conference'].room ?? '');
    const baseUrl = useSelector((state) => state['features/base/config'].deepai_web_server_url ?? '');
    const [st, setSt] = useState(EMPTY);
    // ホバー中の拡大プレビュー。null = 出していない。
    const [preview, setPreview] = useState(null);
    /**
     * サムネイルの位置から拡大枠の位置を決める。
     *
     * 横はサムネイルの中心に合わせ、縦は上に出す。上が足りなければ下へ回す。
     * どちらも画面外へはみ出さないよう最後に画面内へ押し戻す。
     *
     * @param {React.MouseEvent} e - 発生元のイベント。
     * @param {string} src - 拡大して見せる画像。
     * @returns {void}
     */
    const onEnterImage = useCallback((e, src) => {
        const r = e.currentTarget.getBoundingClientRect();
        const above = r.top - PREVIEW_GAP - PREVIEW_H;
        const top = above >= PREVIEW_GAP ? above : r.bottom + PREVIEW_GAP;
        setPreview({
            src,
            left: Math.min(Math.max(PREVIEW_GAP, r.left + (r.width / 2) - (PREVIEW_W / 2)), window.innerWidth - PREVIEW_W - PREVIEW_GAP),
            top: Math.min(Math.max(PREVIEW_GAP, top), window.innerHeight - PREVIEW_H - PREVIEW_GAP)
        });
    }, []);
    const onLeaveImage = useCallback(() => setPreview(null), []);
    // 画面外へ出たり、内容が入れ替わったら残しておかない。
    useEffect(() => {
        if (!preview) {
            return;
        }
        const drop = () => setPreview(null);
        window.addEventListener('scroll', drop, true);
        window.addEventListener('resize', drop);
        return () => {
            window.removeEventListener('scroll', drop, true);
            window.removeEventListener('resize', drop);
        };
    }, [preview]);
    useEffect(() => {
        if (!room) {
            return;
        }
        if (!baseUrl) {
            setSt({ ...EMPTY,
                err: 'config.js に deepai_web_server_url が設定されていません。',
                loading: false });
            return;
        }
        const ac = new AbortController();
        const origin = baseUrl.replace(/\/+$/, '');
        /**
         * 1本 GET する。失敗の種類を、画面に出せる日本語にして投げ直す。
         *
         * fetch が TypeError で落ちるのは CORS 拒否とネットワーク断のどちらでも
         * 同じなので、区別せず両方を挙げる。ここを「読み込めませんでした」だけに
         * すると、設定漏れなのか回線なのかが誰にも分からなくなる。
         *
         * @param {string} url - 取得先。
         * @returns {Promise<any>}
         */
        const get = async (url) => {
            let res;
            try {
                res = await fetch(url, { signal: ac.signal });
            }
            catch (e) {
                if (e?.name === 'AbortError') {
                    throw e;
                }
                logger.warn(`PrejoinPanel: fetch failed for ${url}`, e);
                throw new Error(`${origin} に接続できませんでした。\n`
                    + 'CORS が許可されていないか、サーバーに届いていません。\n'
                    + `詳細: ${e?.message ?? e}`);
            }
            if (res.status === 404) {
                const notFound = new Error('not found');
                notFound.absent = true;
                throw notFound;
            }
            if (res.status === 429) {
                throw new Error('取得の回数制限に達しました。しばらく待ってから開き直してください。');
            }
            if (!res.ok) {
                throw new Error(`${url}\nサーバーが ${res.status} を返しました。`);
            }
            return res.json();
        };
        /**
         * 添付の配列を、そのまま描ける形に整える。
         *
         * url は '/api/...' の相対パス。会議サーバーのオリジンに解決されると 404 に
         * なるので、ここで取得先のオリジンを付ける。
         *
         * @param {any} list - API が返した配列（未定義のこともある）。
         * @returns {IAttachment[]}
         */
        const toAttachments = (list) => (Array.isArray(list) ? list : [])
            .filter((f) => typeof f?.url === 'string' && f.url.length > 0)
            .map((f) => ({ ...f,
            url: new URL(f.url, origin).href }));
        const run = async () => {
            setSt({ ...EMPTY });
            try {
                const assigned = await get(`${origin}/api/meeting-ops/rooms/${encodeURIComponent(room)}/assigned-ai`);
                const businessId = (assigned?.items ?? [])
                    .map((it) => it?.business_id)
                    .find((id) => typeof id === 'string' && id.length > 0);
                if (!businessId) {
                    setSt({ ...EMPTY,
                        absent: true,
                        loading: false });
                    return;
                }
                const biz = await get(`${origin}/api/external/business/${encodeURIComponent(businessId)}`);
                // テーマ画像は飾り。取れなくても本文は出す。
                let themeUrl = null;
                try {
                    const t = await get(`${origin}/api/meeting-ops/rooms/${encodeURIComponent(room)}/theme`);
                    themeUrl = typeof t?.image_url === 'string' && t.image_url ? t.image_url : null;
                }
                catch (e) {
                    if (e?.name === 'AbortError') {
                        return;
                    }
                    logger.warn('PrejoinPanel: テーマ画像を取得できませんでした', e);
                }
                setSt({
                    absent: false,
                    demoVideos: toAttachments(biz?.demoVideos),
                    err: null,
                    loading: false,
                    materials: toAttachments(biz?.materials),
                    // scenario は出さない。要約だけ。
                    summary: typeof biz?.hearing?.scenarioSummary === 'string' ? biz.hearing.scenarioSummary : '',
                    themeUrl,
                    title: typeof biz?.title === 'string' ? biz.title : ''
                });
            }
            catch (e) {
                if (e?.name === 'AbortError') {
                    return;
                }
                if (e?.absent) {
                    setSt({ ...EMPTY,
                        absent: true,
                        loading: false });
                    return;
                }
                setSt({ ...EMPTY,
                    err: String(e?.message ?? e),
                    loading: false });
            }
        };
        run();
        return () => ac.abort();
    }, [room, baseUrl]);
    /**
     * タイトルと説明。説明は未登録なら null なので、そのときは行ごと出さない。
     *
     * @param {IAttachment} f - 対象のファイル。
     * @param {string} fallback - タイトルが空のときに使う名前。
     * @returns {JSX.Element}
     */
    const caption = (f, fallback) => (React.createElement("figcaption", null,
        React.createElement("div", { className: classes.itemTitle }, f.title || fallback),
        f.description && React.createElement("div", { className: classes.itemDescription }, f.description)));
    // 業務が未割り当て / 非公開。運用上ふつうに起きるので何も出さない。
    if (!room || st.absent) {
        return null;
    }
    return (React.createElement("div", { className: classes.root },
        st.themeUrl && (React.createElement("div", { className: classes.bgLayer, style: { backgroundImage: `url("${st.themeUrl}")` } })),
        React.createElement("div", { className: classes.inner },
            st.loading && React.createElement("p", { className: classes.empty }, '読み込み中…'),
            st.err && (React.createElement("div", { className: classes.errorCard },
                React.createElement("h2", { className: classes.errorTitle }, '業務情報を取得できませんでした'),
                React.createElement("div", { className: classes.errorBody }, st.err))),
            !st.loading && !st.err && (React.createElement(React.Fragment, null,
                st.title && (React.createElement(React.Fragment, null,
                    React.createElement("h1", { className: st.themeUrl
                            ? `${classes.businessTitle} ${classes.titleOnImage}`
                            : classes.businessTitle }, st.title),
                    React.createElement("div", { className: classes.titleRule }))),
                React.createElement("section", { className: classes.card },
                    React.createElement("h2", { className: classes.heading }, 'シナリオ概要'),
                    st.summary
                        ? React.createElement("p", { className: classes.summary }, st.summary)
                        : React.createElement("p", { className: classes.empty }, '概要は設定されていません。')),
                React.createElement("section", { className: classes.card },
                    React.createElement("h2", { className: classes.heading }, 'デモ動画'),
                    st.demoVideos.length === 0
                        ? React.createElement("p", { className: classes.empty }, 'デモ動画はありません。')
                        : (React.createElement(Showcase, { classes: classes }, st.demoVideos.map((v, i) => (React.createElement("figure", { className: classes.item, key: String(v.id ?? i) },
                            React.createElement("video", { className: classes.thumb, controls: true, preload: 'metadata', src: v.url }),
                            caption(v, `デモ動画 ${i + 1}`))))))),
                React.createElement("section", { className: classes.card },
                    React.createElement("h2", { className: classes.heading }, '参考資料'),
                    st.materials.length === 0
                        ? React.createElement("p", { className: classes.empty }, '参考資料はありません。')
                        : (React.createElement(Showcase, { classes: classes }, st.materials.map((m, i) => (React.createElement("figure", { className: classes.item, key: String(m.id ?? i) },
                            React.createElement("a", { className: classes.link, href: m.url, rel: 'noopener noreferrer', target: '_blank' }, (m.contentType ?? '').startsWith('image/')
                                ? React.createElement("img", { alt: m.title || m.fileName || '参考資料', className: classes.thumb, loading: 'lazy', 
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onMouseEnter: e => onEnterImage(e, m.url), onMouseLeave: onLeaveImage, src: m.url })
                                : (React.createElement("div", { className: classes.fileBox },
                                    React.createElement("span", { className: classes.fileKind }, kindLabelOf(m))))),
                            caption(m, `参考資料 ${i + 1}`)))))))))),
        preview && createPortal(React.createElement("div", { className: classes.preview, style: { left: preview.left,
                top: preview.top } },
            React.createElement("img", { alt: '', className: classes.previewImg, src: preview.src })), document.body)));
};
export default PrejoinPanel;
